// galaxy/stars.js: millions of stateless GPU stars. Each vertex hashes gl_VertexID
// (PCG) into a population member: old thin disk (sampled from the face-on map),
// thick disk, bulge + long bar, young OB associations along the arms. Light is
// energy-conserving: a star's flux L/d^2 is spread over a small Gaussian
// footprint, so the field converges to the glow instead of reading as sprites.
import * as THREE from 'three';
import { GLSL_BLACKBODY } from '../lib/common.js';
import { GLSL_PCG, GLSL_ARMS, GLSL_ROT } from './glsl.js';
import { GLSL_DISK } from './shade.js';
import { armUniforms, SUN } from './model.js';

const VERT = /* glsl */`
${GLSL_PCG}
${GLSL_ARMS}
${GLSL_ROT}
${GLSL_DISK}
${GLSL_BLACKBODY}
uniform vec4 uPopEnd;      // cumulative counts: old, thick, bulge, young
uniform vec4 uPopL;        // mean luminosity per star (map units * kpc^2)
uniform vec3 uCam, uSun;
uniform float uPixSr, uSizeMin, uSizeMax, uHole, uNear, uGain, uEmax, uKeep, uKeepY;
uniform sampler2D uBg;   // this frame's glow (drawn first)
uniform vec2 uVis;       // contrast window (star peak / background)
uniform int uDustK;
out vec3 vCol;
float lum(vec3 c) { return dot(c, vec3(0.3, 0.5, 0.2)); }
// heavy-tailed luminosity function with mean ~1 (Pareto, alpha a)
float lumf(inout uint s, float a, float cap) { float xm = (a - 1.0) / a; return min(xm * pow(max(rnd(s), 1e-6), -1.0 / a), cap); }
float laplace(inout uint s) { float u = rnd(s) - 0.5; return -sign(u) * log(max(1.0 - 2.0 * abs(u), 1e-6)); }
void cull() { gl_Position = vec4(0.0, 0.0, -2.0, 1.0); gl_PointSize = 0.0; vCol = vec3(0.0); }
void main() {
  int vid = gl_VertexID;
  float fid = float(vid);
  // position.x is always 0; reading it keeps attribute 0 active (Firefox on desktop GL emulates otherwise)
  uint s = pcg(uint(vid) * 2654435761u + 0x9E3779B9u + uint(position.x));
  // stochastic LOD: far away draw fewer, brighter stars (same total light, less grain, less work)
  float kp = fid < uPopEnd.z ? uKeep : uKeepY;
  float hk = float(pcg(uint(vid) ^ 0x5bd1e995u) >> 8) * (1.0 / 16777216.0);
  if (hk > kp) { cull(); return; }
  float wk = smoothstep(kp, kp * 0.85, hk) / (0.925 * kp);
  vec3 p; float T, L;
  if (fid < uPopEnd.x) {
    // old thin disk: exponential-disk proposal, accepted by the map's old light
    bool ok = false; vec2 xz = vec2(0.0);
    for (int k = 0; k < 4; k++) {
      float R = -2.6 * log(max(rnd(s) * rnd(s), 1e-7));
      float ph = 6.2831853 * rnd(s);
      xz = R * vec2(cos(ph), sin(ph));
      float env = 2.0 * exp(-R / 2.6) + 0.1 * exp(-R / 4.5);
      if (R < 17.0 && rnd(s) * env < lum(mapA(xz, 0.0).rgb)) { ok = true; break; }
    }
    if (!ok) { cull(); return; }
    p = vec3(xz.x, 0.25 * laplace(s), xz.y);
    T = mix(3500.0, 6200.0, pow(rnd(s), 1.6));
    L = uPopL.x * lumf(s, 1.6, 200.0);
  } else if (fid < uPopEnd.y) {
    // thick disk (2.2 kpc x 0.9 kpc): old, sparse, warm
    float R = -2.2 * log(max(rnd(s) * rnd(s), 1e-7));
    float ph = 6.2831853 * rnd(s);
    p = vec3(R * cos(ph), 0.9 * laplace(s), R * sin(ph));
    T = mix(3700.0, 5800.0, rnd(s));
    L = uPopL.y * lumf(s, 1.6, 200.0);
  } else if (fid < uPopEnd.z) {
    // boxy bulge / long bar / nuclear disk, in the bar frame
    float c = rnd(s); vec3 b;
    if (c < 0.66) b = vec3(gauss(s) * 0.95, gauss(s) * 0.34, gauss(s) * 0.40);
    else if (c < 0.94) b = vec3((rnd(s) * 2.0 - 1.0) * 4.4 * sqrt(rnd(s)), 0.13 * laplace(s), gauss(s) * 0.28);
    else b = vec3(gauss(s) * 0.12, gauss(s) * 0.04, gauss(s) * 0.09);
    float cb = cos(BARPHI), sb = sin(BARPHI);
    p = vec3(b.x * cb - b.z * sb, b.y, b.x * sb + b.z * cb);
    T = mix(3400.0, 5200.0, pow(rnd(s), 1.3));
    L = uPopL.z * lumf(s, 1.6, 200.0);
  } else {
    // young OB associations: 16 stars share a centre on an arm (map-accepted)
    uint cs = pcg(uint((vid - int(uPopEnd.z)) / 16) * 747796405u + 12345u);
    bool ok = false; vec2 xz = vec2(0.0);
    for (int k = 0; k < 5; k++) {
      float u = rnd(cs); int ai = 0;
      for (int i = 0; i < NARMS - 1; i++) if (u > uArmCum[i]) ai = i + 1;
      vec2 rg = armRange(ai);
      float b = mix(rg.x, rg.y, rnd(cs));
      vec2 a = armAt(ai, b);
      float Ra = exp(a.x), w = max(0.16, uArmInfo[ai].z + 0.042 * (Ra - 6.0)) * 1.25;
      float R = Ra + gauss(cs) * 1.1 * w;
      float ph = b + 1.5707963;
      xz = R * vec2(cos(ph), sin(ph));
      if (rnd(cs) < mapB(xz, 0.0).a * 1.6) { ok = true; break; }
    }
    if (!ok) { cull(); return; }
    float spread = 0.03 + 0.09 * rnd(cs);
    p = vec3(xz.x + gauss(s) * spread, 0.05 * laplace(s), xz.y + gauss(s) * spread);
    float t = rnd(s);
    T = t < 0.06 ? 3600.0 : mix(9000.0, 30000.0, pow(rnd(s), 2.2));
    L = uPopL.w * lumf(s, 1.5, 400.0) * (t < 0.06 ? 3.0 : 1.0);
  }
  // ambient differential rotation (artistic time scale; the Sun's radius co-rotates)
  float r = length(p.xz);
  p.xz = rot2(p.xz, omegaRel(r) * uTime * uFlow * 1e-3);
  vec3 d = uCam - p;
  float dd = dot(d, d);
  // real stars (HYG) own the Sun's neighbourhood while we are near it
  float ds = length(p - uSun);
  float keep = mix(1.0, smoothstep(0.05, 0.2, ds), uHole);
  keep *= smoothstep(uNear, uNear * 3.0, sqrt(dd));
  float E = uGain * keep * wk * L / (dd * uPixSr);
  E = E / (1.0 + E / uEmax);
  if (E < 2e-4) { cull(); return; }
  vec4 clip = projectionMatrix * (modelViewMatrix * vec4(p, 1.0));
  if (clip.w <= 0.0 || abs(clip.x) > clip.w * 1.03 || abs(clip.y) > clip.w * 1.03) { cull(); return; }
  float sz = clamp(uSizeMin * (1.0 + 0.16 * log2(1.0 + E)), uSizeMin, uSizeMax);
  float Ip = E / (0.2488 * sz * sz);
  // only stars that stand out from the unresolved glow behind them are drawn
  float bg = dot(textureLod(uBg, clip.xy / clip.w * 0.5 + 0.5, 0.0).rgb, vec3(0.3, 0.5, 0.2));
  float vis = smoothstep(uVis.x, uVis.y, Ip / max(bg, 1e-6));
  if (vis * E < 2e-4) { cull(); return; }
  float tau = uDustK > 0 ? dustTau(p, uCam, 0.0, uDustK) : 0.0;
  vec3 tr = exp(-tau * vec3(0.95, 1.0, 0.92)); // SPECTRUM: grey-violet dust (as glow.js)
  gl_Position = clip;
  gl_PointSize = sz;
  vec3 bb = blackbody(T);
  // SPECTRUM: old populations read warm-white, never amber (luminance kept; hot young stars keep their blue)
  bb = mix(vec3(dot(bb, vec3(0.2126, 0.7152, 0.0722))), bb, 0.3 + 0.7 * smoothstep(5200.0, 8000.0, T));
  vCol = bb * tr * (Ip * vis);
}`;

const FRAG = /* glsl */`
in vec3 vCol;
void main() {
  vec2 q = gl_PointCoord * 2.0 - 1.0;
  float r2 = dot(q, q);
  if (r2 > 1.0) discard;
  gl_FragColor = vec4(vCol * exp(-3.0 * r2), 1.0);
}`;

// fractions of the star budget per population
const FRAC = { old: 0.50, thick: 0.08, bulge: 0.14, young: 0.28 };

export function makeStars(count, maps) {
  const n = {
    old: Math.round(count * FRAC.old), thick: Math.round(count * FRAC.thick),
    bulge: Math.round(count * FRAC.bulge),
  };
  n.young = Math.floor((count - n.old - n.thick - n.bulge) / 16) * 16;
  const total = n.old + n.thick + n.bulge + n.young;
  const geo = new THREE.BufferGeometry();
  // dummy attribute: three sizes the draw from position.count; the shader never reads it
  geo.setAttribute('position', new THREE.BufferAttribute(new Uint8Array(total), 1));
  geo.setDrawRange(0, total);
  geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 30); // never computed from the dummy attribute
  const U = {
    uMapA: { value: maps.A.texture }, uMapB: { value: maps.B.texture },
    uPopEnd: { value: new THREE.Vector4(n.old, n.old + n.thick, n.old + n.thick + n.bulge, total) },
    uPopL: { value: new THREE.Vector4(1, 1, 1, 1) },
    uCam: { value: new THREE.Vector3() }, uSun: { value: SUN.clone() },
    uPixSr: { value: 1e-6 }, uSizeMin: { value: 3.2 }, uSizeMax: { value: 24 },
    uHole: { value: 1 }, uNear: { value: 0.001 }, uGain: { value: 1 }, uEmax: { value: 3 }, uKeep: { value: 1 }, uKeepY: { value: 1 }, uBg: { value: null }, uVis: { value: new THREE.Vector2(1.0, 3.5) }, uDustK: { value: 2 },
    uTime: { value: 0 }, uFlow: { value: 0 },
    ...armUniforms(),
  };
  const mat = new THREE.ShaderMaterial({
    uniforms: U, vertexShader: VERT, fragmentShader: FRAG,
    transparent: true, depthTest: false, depthWrite: false, blending: THREE.AdditiveBlending,
  });
  const points = new THREE.Points(geo, mat);
  points.frustumCulled = false;
  // per-star mean luminosity = share of each component's total light (map units * kpc^2)
  // totals are rough integrals of the model (tuned by eye); frac = resolved share
  const setLight = ({ old = 0.35, thick = 0.5, bulge = 0.3, young = 0.6 } = {}, tot = { old: 90, thick: 6, bulge: 7, young: 30 }) => {
    U.uPopL.value.set(
      (old * tot.old) / Math.max(n.old, 1), (thick * tot.thick) / Math.max(n.thick, 1),
      (bulge * tot.bulge) / Math.max(n.bulge, 1), (young * tot.young) / Math.max(n.young, 1));
  };
  setLight();
  return { points, U, n, total, setLight, dispose() { geo.dispose(); mat.dispose(); } };
}
